import React, { useState, useEffect } from 'react';
import { isPlatformAdmin } from '../utils/roles';
import { Users, Plus, Edit2, Trash2, Key, Monitor, X, Check } from 'lucide-react';
import { userService, dashboardService } from '../services';
import api from '../services/api';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDashboardAssignModal, setShowDashboardAssignModal] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserDashboard, setSelectedUserDashboard] = useState(null);
  const [userDashboards, setUserDashboards] = useState([]);
  const [dashboardDevices, setDashboardDevices] = useState([]);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState([]);
  
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    company_id: ''
  });

  const [dashboardAssignForm, setDashboardAssignForm] = useState({
    dashboard_id: '',
    device_id: '',
    thingsboard_device_id: '',
    use_websocket: false
  });

  const [deviceForm, setDeviceForm] = useState({
    device_name: '',
    thingsboard_device_id: '',
    is_default: false
  });

  // Device picker: devices the assigned user can see in ThingsBoard
  const [availableDevices, setAvailableDevices] = useState([]);
  const [availableScope, setAvailableScope] = useState(null);
  const [availableCustomer, setAvailableCustomer] = useState(null);
  const [availableMessage, setAvailableMessage] = useState('');
  const [availableLoading, setAvailableLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [defaultDeviceId, setDefaultDeviceId] = useState(null);
  const [addingDevices, setAddingDevices] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const companyName = (id) => {
    const match = companies.find((c) => String(c.id) === String(id));
    return match ? match.name : 'Unknown';
  };

  const visibleAvailableDevices = availableDevices.filter((d) => {
    const q = deviceSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.label || '').toLowerCase().includes(q) ||
      (d.profile || '').toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    loadUsers();
    loadDashboards();
    loadCompanies();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Which company a user belongs to decides what they see and which machines
  // they can reach, so the picker needs the list.
  const loadCompanies = async () => {
    try {
      const res = await api.get('/branding/companies');
      setCompanies(res.data);
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  const loadDashboards = async () => {
    try {
      const data = await dashboardService.getAll();
      setDashboards(data);
    } catch (error) {
      console.error('Failed to load dashboards:', error);
    }
  };

  const loadUserDashboards = async (userId) => {
    try {
      const response = await fetch(`/api/dashboards?user_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const userDashboards = await response.json();
        setUserDashboards(userDashboards);
      }
    } catch (error) {
      console.error('Failed to load user dashboards:', error);
      setUserDashboards([]);
    }
  };

  const loadDashboardDevices = async (userDashboardId) => {
    try {
      const response = await fetch(`/api/user-dashboards/${userDashboardId}/devices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const devices = await response.json();
        setDashboardDevices(devices);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
      setDashboardDevices([]);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      username: '',
      email: '',
      password: '',
      role: 'user',
      company_id: ''
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      company_id: user.company_id || ''
    });
    setShowUserModal(true);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        await userService.update(editingUser.id, userForm);
        setSuccess('User updated successfully');
      } else {
        await userService.create(userForm);
        setSuccess('User created successfully');
      }
      setShowUserModal(false);
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? All dashboard assignments will be removed.')) return;

    try {
      await userService.delete(userId);
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (error) {
      setError('Failed to delete user');
    }
  };

  const handleOpenDashboardAssign = (user) => {
    setSelectedUser(user);
    setDashboardAssignForm({
      dashboard_id: '',
      device_id: '',
      thingsboard_device_id: ''
    });
    loadUserDashboards(user.id);
    setShowDashboardAssignModal(true);
  };

  const handleAssignDashboard = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await dashboardService.assignToUser(dashboardAssignForm.dashboard_id, {
        user_id: selectedUser.id,
        device_id: dashboardAssignForm.device_id || null,
        thingsboard_device_id: dashboardAssignForm.thingsboard_device_id || null,
        use_websocket: dashboardAssignForm.use_websocket
      });
      setSuccess(`Dashboard assigned to ${selectedUser.username}`);
      loadUserDashboards(selectedUser.id);
      setDashboardAssignForm({
        dashboard_id: '',
        device_id: '',
        thingsboard_device_id: '',
        use_websocket: false
      });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to assign dashboard');
    }
  };

  const handleUnassignDashboard = async (dashboardId) => {
    if (!window.confirm('Remove this dashboard assignment?')) return;

    try {
      await dashboardService.unassignFromUser(dashboardId, selectedUser.id);
      setSuccess('Dashboard unassigned');
      loadUserDashboards(selectedUser.id);
    } catch (error) {
      setError('Failed to unassign dashboard');
    }
  };

  const loadAvailableDevices = async (userDashboardId) => {
    setAvailableLoading(true);
    setAvailableMessage('');
    try {
      const response = await fetch(`/api/user-dashboards/${userDashboardId}/available-devices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setAvailableDevices([]);
        setAvailableMessage(data.error || 'Could not load devices from ThingsBoard.');
        return;
      }

      setAvailableDevices(data.devices || []);
      setAvailableScope(data.scope || null);
      setAvailableCustomer(data.customerTitle || null);
      // Only the explanatory scopes carry a message; a normal list has none.
      setAvailableMessage(data.message || '');
    } catch (error) {
      setAvailableDevices([]);
      setAvailableMessage('Could not load devices from ThingsBoard: ' + error.message);
    } finally {
      setAvailableLoading(false);
    }
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDeviceIds((prev) => {
      const next = prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId];
      // Dropping the device that was marked default clears the default.
      if (!next.includes(deviceId) && defaultDeviceId === deviceId) {
        setDefaultDeviceId(null);
      }
      return next;
    });
  };

  const handleAddSelectedDevices = async () => {
    if (selectedDeviceIds.length === 0) return;
    setError('');
    setSuccess('');
    setAddingDevices(true);

    const failures = [];
    let added = 0;

    for (const deviceId of selectedDeviceIds) {
      const device = availableDevices.find((d) => d.thingsboard_device_id === deviceId);
      if (!device) continue;
      try {
        const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/devices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            device_name: device.label || device.name,
            thingsboard_device_id: deviceId,
            is_default: defaultDeviceId === deviceId
          })
        });

        const data = await response.json();
        if (!response.ok) {
          failures.push(`${device.name}: ${data.error || 'failed'}`);
        } else {
          added += 1;
        }
      } catch (err) {
        failures.push(`${device.name}: ${err.message}`);
      }
    }

    setSelectedDeviceIds([]);
    setDefaultDeviceId(null);
    await loadDashboardDevices(selectedUserDashboard.id);
    await loadAvailableDevices(selectedUserDashboard.id);
    setAddingDevices(false);

    if (added > 0) {
      setSuccess(`Added ${added} device${added === 1 ? '' : 's'}`);
      setTimeout(() => setSuccess(''), 3000);
    }
    if (failures.length > 0) {
      setError(failures.join(' | '));
      setTimeout(() => setError(''), 8000);
    }
  };

  const handleManageDevices = async (userDashboard) => {
    setSelectedUserDashboard(userDashboard);
    setDeviceSearch('');
    setSelectedDeviceIds([]);
    setDefaultDeviceId(null);
    setShowManualAdd(false);
    setAvailableDevices([]);
    setAvailableScope(null);
    setAvailableCustomer(null);
    setAvailableMessage('');
    await loadDashboardDevices(userDashboard.id);
    await loadAvailableDevices(userDashboard.id);
    setDeviceForm({
      device_name: '',
      thingsboard_device_id: '',
      is_default: false
    });
    setShowDeviceModal(true);
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(deviceForm)
      });

      const data = await response.json();

      if (!response.ok) {
        // Extract error message from response
        const errorMessage = data.error || 'Failed to add device';
        throw new Error(errorMessage);
      }

      setSuccess('Device added successfully');
      await loadDashboardDevices(selectedUserDashboard.id);
      await loadAvailableDevices(selectedUserDashboard.id);
      setDeviceForm({
        device_name: '',
        thingsboard_device_id: '',
        is_default: false
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
      // Clear error message after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Remove this device?')) return;

    try {
      const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/devices/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete device');

      setSuccess('Device removed');
      await loadDashboardDevices(selectedUserDashboard.id);
      await loadAvailableDevices(selectedUserDashboard.id);
    } catch (error) {
      setError(error.message);
    }
  };

  // Check every binding in this assignment against ThingsBoard, and optionally
  // repair the ones that only drifted. Dry run by default: the admin sees what
  // would change before anything is written.
  const handleReconcile = async (apply) => {
    setReconciling(true);
    setError('');
    try {
      const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/reconcile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apply: !!apply })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to check bindings');
      setReconcileResult(data);
      if (apply) {
        setSuccess(data.applied.length
          ? `Repaired ${data.applied.length} binding(s)`
          : 'Nothing to repair');
        await loadDashboardDevices(selectedUserDashboard.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setReconciling(false);
    }
  };

  const handleRenameDevice = async (deviceId, name) => {
    const clean = String(name || '').trim();
    if (!clean) { setError('Name cannot be empty'); return; }
    try {
      const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/devices/${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ device_name: clean })
      });
      if (!response.ok) throw new Error('Failed to rename device');
      setRenamingId(null);
      setSuccess('Device renamed');
      await loadDashboardDevices(selectedUserDashboard.id);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSetDefaultDevice = async (deviceId) => {
    try {
      const response = await fetch(`/api/user-dashboards/${selectedUserDashboard.id}/devices/${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_default: true })
      });

      if (!response.ok) throw new Error('Failed to set default');

      setSuccess('Default device updated');
      await loadDashboardDevices(selectedUserDashboard.id);
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="section-header">
        <div>
          <h2><Users size={24} /> User Management</h2>
          <p>Manage users, ThingsBoard mappings, and dashboard assignments</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateUser}>
          <Plus size={20} /> Add User
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Company</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td><strong>{user.username}</strong></td>
                <td>{user.email}</td>
                <td><span className={`badge badge-${user.role}`}>{user.role}</span></td>
                <td>
                  {user.company_id ? (
                    <span className="thingsboard-mapped">
                      <Key size={14} /> {companyName(user.company_id)}
                    </span>
                  ) : (
                    <span className="text-muted">None</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleOpenDashboardAssign(user)}
                      title="Manage Dashboards"
                    >
                      <Monitor size={16} /> Dashboards
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Delete User"
                      disabled={isPlatformAdmin(user.role)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmitUser} className="modal-body">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      required
                      disabled={editingUser}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password {editingUser && '(leave blank to keep current)'}</label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      required={!editingUser}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="company_admin">Company administrator</option>
                      <option value="platform_admin">PrimeHive administrator</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Company</h3>
                <p className="form-help">
                  Which company this person belongs to. It decides the name and logo they see,
                  and which machines they can reach.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="user-company">Company</label>
                    <select
                      id="user-company"
                      value={userForm.company_id}
                      onChange={(e) => setUserForm({ ...userForm, company_id: e.target.value })}
                      required={userForm.role !== 'platform_admin'}
                    >
                      <option value="">
                        {userForm.role === 'platform_admin' ? 'None (PrimeHive staff)' : 'Choose a company...'}
                      </option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>


              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dashboard Assignment Modal */}
      {showDashboardAssignModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDashboardAssignModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Monitor size={20} /> Manage Dashboards for {selectedUser.username}</h2>
              <button className="modal-close" onClick={() => setShowDashboardAssignModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Assign New Dashboard */}
              <div className="form-section">
                <h3>Assign New Dashboard</h3>
                <form onSubmit={handleAssignDashboard}>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 2 }}>
                      <label>Select Dashboard *</label>
                      <select
                        value={dashboardAssignForm.dashboard_id}
                        onChange={(e) => setDashboardAssignForm({ ...dashboardAssignForm, dashboard_id: e.target.value })}
                        required
                      >
                        <option value="">Choose a dashboard...</option>
                        {dashboards.map((dash) => (
                          <option key={dash.dashboard_id} value={dash.dashboard_id}>
                            {dash.name} ({dash.dashboard_id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                        <Plus size={16} /> Assign Dashboard
                      </button>
                    </div>
                  </div>
                  <div className="form-row" style={{ marginTop: '10px' }}>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={dashboardAssignForm.use_websocket}
                          onChange={(e) => setDashboardAssignForm({ ...dashboardAssignForm, use_websocket: e.target.checked })}
                          style={{ width: 'auto', cursor: 'pointer' }}
                        />
                        <span>Use WebSocket for real-time data updates</span>
                      </label>
                      <small style={{ color: '#888', marginLeft: '30px', display: 'block' }}>
                        Enable for live telemetry updates without polling. Requires WebSocket support in ThingsBoard.
                      </small>
                    </div>
                  </div>
                </form>
              </div>

              {/* Assigned Dashboards List */}
              <div className="form-section">
                <h3>Assigned Dashboards</h3>
                {userDashboards.length === 0 ? (
                  <p className="text-muted">No dashboards assigned yet. Assign one above to get started.</p>
                ) : (
                  <div className="dashboard-assignments-list">
                    {userDashboards.map((ud) => (
                      <div key={ud.user_dashboard_id} className="dashboard-assignment-item">
                        <div className="dashboard-info">
                          <h4>{ud.name}</h4>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span className="dashboard-id-badge">{ud.dashboard_id}</span>
                            {ud.use_websocket && (
                              <span className="websocket-badge" title="Using WebSocket for real-time updates">
                                ⚡ WebSocket
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="dashboard-actions">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleManageDevices({ id: ud.user_dashboard_id, dashboard_name: ud.name })}
                          >
                            <Monitor size={14} /> Manage Devices
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleUnassignDashboard(ud.dashboard_id)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Management Modal */}
      {showDeviceModal && selectedUserDashboard && (
        <div className="modal-overlay" onClick={() => setShowDeviceModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Monitor size={20} /> Manage Devices</h2>
              <button className="modal-close" onClick={() => setShowDeviceModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Error Message */}
              {error && (
                <div className="error-message" style={{ 
                  background: '#e74c3c', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '5px', 
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}
              
              {/* Success Message */}
              {success && (
                <div className="success-message" style={{ 
                  background: '#2ecc71', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '5px', 
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {success}
                </div>
              )}
              
              {/* Device picker — devices this user can see in ThingsBoard */}
              <div className="form-section">
                <h3>Add Devices</h3>

                <div className="dp-toolbar">
                  <input
                    type="text"
                    className="dp-search"
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    placeholder="Filter by device name…"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => loadAvailableDevices(selectedUserDashboard.id)}
                    disabled={availableLoading}
                  >
                    {availableLoading ? 'Loading…' : 'Refresh'}
                  </button>
                  {availableScope === 'customer' && availableCustomer && (
                    <span className="dp-scope">Customer: <b>{availableCustomer}</b></span>
                  )}
                  {availableScope === 'tenant' && (
                    <span className="dp-scope dp-scope-warn">
                      Tenant-wide list — these credentials are not scoped to a customer
                    </span>
                  )}
                </div>

                {availableMessage && <div className="dp-message">{availableMessage}</div>}

                {availableLoading ? (
                  <p className="text-muted">Loading devices from ThingsBoard…</p>
                ) : visibleAvailableDevices.length === 0 ? (
                  <p className="text-muted">
                    {availableDevices.length === 0
                      ? 'No devices available to add.'
                      : 'No devices match that filter.'}
                  </p>
                ) : (
                  <div className="dp-scroll">
                    <table className="dp-table">
                      <thead>
                        <tr>
                          <th style={{ width: '36px' }}></th>
                          <th>Device</th>
                          <th style={{ width: '150px' }}>Profile</th>
                          <th style={{ width: '90px' }}>State</th>
                          <th style={{ width: '76px' }}>Default</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleAvailableDevices.map((d) => {
                          const checked = selectedDeviceIds.includes(d.thingsboard_device_id);
                          return (
                            <tr
                              key={d.thingsboard_device_id}
                              className={d.already_added ? 'dp-row-added' : ''}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={d.already_added || checked}
                                  disabled={d.already_added}
                                  title={d.already_added ? 'Already added to this dashboard' : 'Select to add'}
                                  onChange={() => toggleDeviceSelection(d.thingsboard_device_id)}
                                />
                              </td>
                              <td>
                                <div className="dp-name">
                                  {d.label || d.name}
                                  {d.already_added && <span className="dp-added">Added</span>}
                                </div>
                                {d.label && <div className="dp-sub">{d.name}</div>}
                                <div className="dp-uuid">{d.thingsboard_device_id}</div>
                              </td>
                              <td>{d.profile || '—'}</td>
                              <td>
                                <span className={`dp-dot ${d.active ? 'dp-on' : 'dp-off'}`}></span>
                                {d.active ? 'Online' : 'Offline'}
                              </td>
                              <td>
                                <input
                                  type="radio"
                                  name="dp-default"
                                  checked={defaultDeviceId === d.thingsboard_device_id}
                                  disabled={d.already_added || !checked}
                                  title={
                                    d.already_added
                                      ? 'Use Set Default in the list below'
                                      : 'Select the device first'
                                  }
                                  onChange={() => setDefaultDeviceId(d.thingsboard_device_id)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="dp-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={selectedDeviceIds.length === 0 || addingDevices}
                    onClick={handleAddSelectedDevices}
                  >
                    <Plus size={16} />
                    {addingDevices
                      ? ' Adding…'
                      : ` Add selected${selectedDeviceIds.length ? ` (${selectedDeviceIds.length})` : ''}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setShowManualAdd(!showManualAdd)}
                  >
                    {showManualAdd ? 'Hide manual entry' : 'Enter a device ID manually'}
                  </button>
                </div>

                {/* Fallback: paste a ThingsBoard device ID (e.g. a device not yet
                    assigned to the customer, so it cannot appear in the list above) */}
                {showManualAdd && (
                  <form onSubmit={handleAddDevice} className="dp-manual">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Device Name *</label>
                        <input
                          type="text"
                          value={deviceForm.device_name}
                          onChange={(e) => setDeviceForm({ ...deviceForm, device_name: e.target.value })}
                          required
                          placeholder="e.g., Production Line A"
                        />
                      </div>
                      <div className="form-group">
                        <label>ThingsBoard Device ID *</label>
                        <input
                          type="text"
                          value={deviceForm.thingsboard_device_id}
                          onChange={(e) => setDeviceForm({ ...deviceForm, thingsboard_device_id: e.target.value })}
                          required
                          placeholder="Device ID from ThingsBoard"
                        />
                      </div>
                      <div className="form-group" style={{ flex: 0.5 }}>
                        <label>&nbsp;</label>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                          <Plus size={16} /> Add
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={deviceForm.is_default}
                          onChange={(e) => setDeviceForm({ ...deviceForm, is_default: e.target.checked })}
                        />
                        Set as default device
                      </label>
                    </div>
                  </form>
                )}
              </div>

              {/* Devices List */}
              <div className="form-section">
                <h3>
                  Devices
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    style={{ marginLeft: 10 }}
                    disabled={reconciling}
                    onClick={() => handleReconcile(false)}
                    title="Compare each binding against ThingsBoard by name and by id"
                  >
                    {reconciling ? 'Checking…' : 'Check bindings'}
                  </button>
                </h3>

                {reconcileResult && (
                  <div className="reconcile-result">
                    <p className="text-muted">
                      Checked {reconcileResult.checked} binding(s) against ThingsBoard
                      ({reconcileResult.scope} scope).{' '}
                      {reconcileResult.findings.length === 0 && 'All healthy.'}
                    </p>
                    {reconcileResult.findings.map((f) => (
                      <div key={f.id} className="reconcile-finding">
                        <strong>{f.device_name}</strong>{' '}
                        <span className={`binding-badge b-${f.binding}`}>{f.binding}</span>{' '}
                        {f.binding === 'stale' && (
                          <span>re-created in ThingsBoard — repoint to <code>{f.suggested_device_id}</code></span>
                        )}
                        {f.binding === 'renamed' && (
                          <span>renamed in ThingsBoard — update name to <code>{f.suggested_device_name}</code></span>
                        )}
                        {f.binding === 'missing' && (
                          <span>no device with this name or id exists — remove the binding, or re-add the machine</span>
                        )}
                        {f.assigned_to_customer === false && (
                          <span> · not assigned to a customer, so the operator cannot see it — assign it in ThingsBoard first</span>
                        )}
                        {f.blocked && <span className="text-danger"> · {f.blocked}</span>}
                      </div>
                    ))}
                    {reconcileResult.fixable > 0 && !reconcileResult.applied_changes && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={reconciling}
                        onClick={() => handleReconcile(true)}
                      >
                        Repair {reconcileResult.fixable} binding(s)
                      </button>
                    )}
                  </div>
                )}

                {dashboardDevices.length === 0 ? (
                  <p className="text-muted">No devices assigned. Add one above.</p>
                ) : (
                  <div className="devices-list">
                    {dashboardDevices.map((device) => (
                      <div key={device.id} className="device-item">
                        <div className="device-info">
                          {renamingId === device.id ? (
                            <div className="device-rename">
                              <input
                                type="text"
                                value={renameValue}
                                autoFocus
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameDevice(device.id, renameValue);
                                  if (e.key === 'Escape') setRenamingId(null);
                                }}
                              />
                              <button className="btn btn-sm btn-primary"
                                onClick={() => handleRenameDevice(device.id, renameValue)}>Save</button>
                              <button className="btn btn-sm btn-secondary"
                                onClick={() => setRenamingId(null)}>Cancel</button>
                            </div>
                          ) : (
                            <h4>
                              {device.device_name}
                              {device.binding && device.binding !== 'ok' && (
                                <span className={`binding-badge b-${device.binding}`}>{device.binding}</span>
                              )}
                              <button
                                className="btn-rename"
                                title="Rename. Note: a machine_label set on the device takes priority in the picker."
                                onClick={() => { setRenamingId(device.id); setRenameValue(device.device_name || ''); }}
                              >rename</button>
                            </h4>
                          )}
                          <span className="device-id">{device.thingsboard_device_id}</span>
                          {device.is_default && <span className="default-badge">Default</span>}
                        </div>
                        <div className="device-actions">
                          {!device.is_default && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleSetDefaultDevice(device.id)}
                              title="Set as default"
                            >
                              <Check size={14} /> Set Default
                            </button>
                          )}
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleDeleteDevice(device.id)}
                            title="Remove device"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
